import { useMemo } from 'react';
import {
  Flex,
  FlexColumn,
  CopyTestLinkButton,
  Loading,
  TestLineageNavButton,
  GenerateAIButton,
} from '@elementary/common-ui/components';
import useGraphqlQuery from '@elementary/common-ui/hooks/useGraphqlQuery';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import { ModelRun, ModelRuns } from '@elementary/common-ui/types';
import { isInSaaSMode } from '@elementary/common-ui/utils/common';
import { Typography } from '@mui/material';
import dayjs from 'dayjs';
import { MODEL_EXEC_HISTORY } from '@elementary/common-ui/constants/queryKeys';
import { DEFAULT_DAYS_BACK } from '@elementary/common-ui/constants';
import { getModelExecutionHistoryQuery } from '../api';
import { ModelExecutionHistoryResponse } from '../types';
import { toModelRunStatus } from '../utils';
import ModelRunsChart from './ModelRunsChart';

const mapExecutionHistoryToModelRuns = (
  executions: ModelExecutionHistoryResponse['tableAsset']['executionHistory'],
  materialization?: string,
): ModelRun[] =>
  executions.map((execution) => ({
    id: execution.id,
    time_utc: execution.executionTimestamp || '',
    status: toModelRunStatus(execution.status),
    execution_time: execution.executionTime,
    materialization: materialization || '',
    full_refresh: execution.isFullRebuild ?? false,
  }));

const ModelRunsExpandedRow = ({
  rowData,
  onOpenAiChat,
  aiOptimizationEnabled,
}: {
  rowData: ModelRuns;
  onOpenAiChat: (rowData: ModelRuns) => void;
  aiOptimizationEnabled: boolean;
}) => {
  const isSaaS = isInSaaSMode();
  const selectedEnvId = useSelectedEnv();

  const { startTime, endTime } = useMemo(
    () => ({
      startTime: dayjs().subtract(DEFAULT_DAYS_BACK, 'day').toISOString(),
      endTime: dayjs().toISOString(),
    }),
    [],
  );

  const { data: executionHistoryData, isFetching: isExecutionHistoryFetching } =
    useGraphqlQuery<ModelExecutionHistoryResponse>({
      query: getModelExecutionHistoryQuery,
      queryKey: [MODEL_EXEC_HISTORY, selectedEnvId, rowData.unique_id],
      variables: {
        id: rowData.unique_id,
        startTime,
        endTime,
      },
      enabled: isSaaS,
      envId: selectedEnvId,
    });

  const chartData = useMemo(() => {
    if (!isSaaS) {
      return rowData.runs;
    }

    return mapExecutionHistoryToModelRuns(
      executionHistoryData?.tableAsset.executionHistory || [],
      rowData.asset_metadata?.materialization,
    );
  }, [
    executionHistoryData?.tableAsset.executionHistory,
    isSaaS,
    rowData.asset_metadata?.materialization,
    rowData.runs,
  ]);

  return (
    <FlexColumn sx={{ gap: 2 }}>
      <Flex sx={{ justifyContent: 'space-between' }}>
        <Typography sx={{ fontWeight: 500, color: 'common.gray2' }}>
          Model execution time and status over time
        </Typography>
        <Flex sx={{ gap: 1, alignItems: 'center' }}>
          <TestLineageNavButton modelId={rowData.unique_id} />
          <CopyTestLinkButton testId={rowData.unique_id} />
          {aiOptimizationEnabled && (
            <GenerateAIButton onClick={() => onOpenAiChat(rowData)}>
              AI Optimization
            </GenerateAIButton>
          )}
        </Flex>
      </Flex>
      {isSaaS && isExecutionHistoryFetching ? (
        <Loading size={24} />
      ) : (
        <ModelRunsChart data={chartData} />
      )}
    </FlexColumn>
  );
};

export default ModelRunsExpandedRow;
